export const PAGE_GUIDANCE = {
  dashboard: {
    title: "Dashboard",
    // #891: if dashboard allocations become editable, rewrite this signpost in
    // the same change; it currently sends planning edits back to their sources.
    purpose:
      "This is your at-a-glance readout. Forecast supplies the future timeline and surplus-bucket plan; actual spending and balances come from linked accounts. Change the spending split in Financial Analysis, surplus buckets in Forecast, goals in Goals, and account data in Connected Banks.",
    action: { destination: "forecast", label: "Adjust your buckets" },
    sections: [
      {
        id: "allocation-snapshot",
        title: "Allocation snapshot",
        note: "Compare recent spending with your saved spending split. Change that split in Financial Analysis.",
      },
      {
        id: "pay-period",
        title: "This pay period",
        note: "See income, spending, and money still available before the next paycheck. Categorize transactions to improve the totals.",
      },
      {
        id: "forecast-preview",
        title: "Forecast preview",
        note: "See the next projected pay periods from Forecast. Update recurring items or your pay schedule when dates or amounts are wrong.",
      },
      {
        id: "plan",
        title: "Plan at a glance",
        note: "Review how available money is assigned. Change surplus buckets on Forecast and goal targets on Goals.",
      },
      {
        id: "spending",
        title: "Spending this period",
        note: "See which parts of the plan have been used so far. Open Transactions to review or recategorize the activity behind a total.",
      },
      {
        id: "activity",
        title: "Accounts, goals, and recent activity",
        note: "Use these cards as shortcuts to the source pages where account names, goals, and transactions are managed.",
      },
    ],
  },
  transactions: {
    title: "Transactions",
    purpose:
      "Review the account activity Fomo-Fi uses for spending, budgets, forecasts, and financial analysis.",
    sections: [
      {
        id: "filters",
        title: "Filters and totals",
        note: "Narrow the list by date, account, category, type, or amount. The summary updates to the same filtered set.",
      },
      {
        id: "activity",
        title: "Transaction activity",
        note: "Open a row to inspect its account and category. Correct categories so every downstream view uses better data.",
      },
      {
        id: "review-rules",
        title: "Review and rules",
        note: "Use bulk review for repeated corrections, then create a categorization rule when future matching transactions should follow the same choice.",
      },
    ],
  },
  budgets: {
    title: "Budgets",
    purpose:
      "Turn your monthly income into category targets and compare those targets with actual spending.",
    sections: [
      {
        id: "overview",
        title: "Monthly overview",
        note: "Compare budgeted and spent amounts for the selected month. Expand a group to see the categories behind it.",
      },
      {
        id: "pay-period",
        title: "Pay-period view",
        note: "Break the monthly plan into the paycheck window you are living in now.",
      },
      {
        id: "alignment",
        title: "Spending-split alignment",
        note: "Compare actual spending with the split saved in Financial Analysis. Change the split there, not on this report.",
      },
      {
        id: "setup",
        title: "Budget setup",
        note: "Add or adjust category targets. A budget is a limit for a category; Forecast buckets assign projected surplus to priorities.",
      },
    ],
  },
  calendar: {
    title: "Calendar",
    purpose: "See the same projected income and bills used by Forecast, arranged by date.",
    sections: [
      {
        id: "views",
        title: "Month, pay periods, and list",
        note: "Switch views without changing the underlying events. Use the format that makes timing easiest to understand.",
      },
      {
        id: "pay-period-context",
        title: "Pay-period context",
        note: "The context bar shows where the selected date falls between paychecks and how much is projected for that period.",
      },
      {
        id: "day-details",
        title: "Day details",
        note: "Select a date to inspect its expected income, bills, and projected balance. Correct recurring items when an event is wrong.",
      },
    ],
  },
  goals: {
    title: "Goals",
    purpose:
      "Create savings targets, record progress, and see how long each goal may take at its current pace.",
    sections: [
      {
        id: "summary",
        title: "Progress summary",
        note: "See total saved, total targeted, and the next goal milestone across your active goals.",
      },
      {
        id: "goal-cards",
        title: "Goal cards",
        note: "Each card shows the target, current balance, deadline, and estimated time to reach it.",
      },
      {
        id: "contributions",
        title: "Contributions",
        note: "Record money already moved toward a goal. Planning an amount and recording a contribution are separate actions.",
      },
    ],
  },
  forecast: {
    title: "Forecast",
    purpose:
      "Look ahead from today using linked balances, expected income, recurring bills, recent spending, and your saved plan.",
    sections: [
      {
        id: "equation",
        title: "Money available",
        note: "Start with spendable balance, subtract bills and projected variable spending, and see the surplus left to assign.",
      },
      {
        id: "allocations",
        title: "Surplus allocations",
        note: "Decide how projected surplus should be divided. Edits here change the plan, not money in a bank account.",
      },
      {
        id: "timeline",
        title: "Forecast timeline",
        note: "Follow projected deposits, bills, and balances in date order. Update recurring items or your pay schedule when the timing is wrong.",
      },
      {
        id: "insights",
        title: "Forecast insights",
        note: "Review warnings and opportunities produced from this projection before changing the plan.",
      },
    ],
  },
  insights: {
    title: "Insights",
    purpose:
      "Find patterns and possible improvements in the account activity Fomo-Fi has analyzed.",
    sections: [
      {
        id: "opportunities",
        title: "Opportunities",
        note: "Start with suggestions that could free money or make the current plan more resilient.",
      },
      {
        id: "flexible-spending",
        title: "Flexible spending",
        note: "Explore the discretionary categories contributing most to recent spending.",
      },
      {
        id: "subscriptions",
        title: "Subscriptions",
        note: "Review repeating charges and decide which ones still earn a place in your plan.",
      },
      {
        id: "adjustments",
        title: "Adjustment choices",
        note: "Choose where a confirmed saving should go. Fomo-Fi shows options; it does not move money for you.",
      },
    ],
  },
  financialAnalysis: {
    title: "Financial Analysis",
    purpose:
      "Review what Fomo-Fi identified across the available transaction history before those patterns shape your forecast.",
    sections: [],
  },
  debt: {
    title: "Debt Eliminator",
    purpose:
      "See the payments from your bank that look like debts, add the ones you still owe, and get a payoff plan for them.",
    sections: [
      {
        id: "found",
        title: "Payments that look like debts",
        note: "Regular payments from your checking account that look like a card or a loan. Add the ones you still owe; a mortgage stays with your bills unless you add it.",
      },
      {
        id: "debts",
        title: "Your debts",
        note: "For each debt you add, enter the balance, rate and minimum from your statement. The payoff plan needs all three.",
      },
      {
        id: "strategy",
        title: "Payoff strategy",
        note: "Shows once you've added a debt. Snowball pays the smallest balance first; avalanche pays the highest rate first.",
      },
      {
        id: "summary",
        title: "Payoff summary",
        note: "Shows once you've added a debt: your debt-free date, the interest you'd pay, and what an extra payment does.",
      },
      {
        id: "timeline",
        title: "Timeline and insights",
        note: "Shows once you've added a debt: the projected balance going down, with milestones and warnings.",
      },
    ],
  },
  payment: {
    title: "Payment",
    purpose: "Start a trial, choose a plan, or manage the subscription attached to your account.",
    sections: [
      {
        id: "plan",
        title: "Plan choice",
        note: "Compare billing intervals and review the trial and renewal terms before continuing to Stripe.",
      },
      {
        id: "billing",
        title: "Billing status",
        note: "Open the billing portal to update payment details, switch plans when available, or cancel future renewal.",
      },
    ],
  },
  recurring: {
    title: "Recurring",
    purpose: "Review the repeating income and expenses that drive forecast dates and amounts.",
    sections: [
      {
        id: "expected-items",
        title: "Expected items",
        note: "Confirm the merchant, amount, frequency, and next date for each repeating item.",
      },
      {
        id: "forecast-effect",
        title: "Forecast effect",
        note: "Active recurring items appear on Forecast and Calendar. Pause or correct an item when it should no longer be projected.",
      },
    ],
  },
  settings: {
    // FP-127(c), AN-46: the screen's name, SETTINGS_LABEL_COPY ("Profile") in
    // @fomofi/shared. A literal here: this file is byte-identical in web and mobile.
    title: "Profile",
    purpose:
      "Manage the personal details, preferences, billing, connections, and privacy controls for your account.",
    sections: [
      {
        id: "profile",
        title: "Profile",
        note: "Review the identity attached to this Fomo-Fi account. Sign-in credentials are managed through Clerk.",
      },
      {
        id: "appearance-preferences",
        title: "Appearance and preferences",
        note: "Choose how the app looks and which supported page opens by default.",
      },
      {
        id: "pay-schedule",
        title: "Pay schedule",
        note: "Keep paycheck timing current because Forecast, Calendar, and pay-period views use it.",
      },
      {
        id: "subscription",
        title: "Subscription",
        note: "Review plan status and use Stripe's billing portal for payment-method or cancellation changes.",
      },
      {
        id: "connected-accounts",
        title: "Connected accounts",
        note: "Open bank management to connect, rename, refresh, or disconnect the accounts supplying transaction data.",
      },
      {
        id: "privacy",
        title: "Privacy and data",
        note: "Export a copy of your data or begin permanent account deletion. Read each confirmation before continuing.",
      },
    ],
  },
  banks: {
    title: "Connected Banks",
    purpose: "Control which linked financial accounts supply balances and transactions to Fomo-Fi.",
    sections: [
      {
        id: "connections",
        title: "Connections",
        note: "Connect another institution or inspect the accounts already linked through Plaid.",
      },
      {
        id: "maintenance",
        title: "Rename, refresh, or disconnect",
        note: "Rename changes only the label in Fomo-Fi. Refresh requests current data; disconnect stops future syncing for that connection.",
      },
    ],
  },
} as const;

export type PageGuidanceId = keyof typeof PAGE_GUIDANCE;
